import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getStaffPerformance } from '../lib/api';
import { getUser } from '../lib/auth';
import MobileNav from '../components/MobileNav';
import { ArrowLeft, Clock, TrendingUp, TrendingDown, CheckCircle2, Lightbulb, MapPin, BarChart3, Minus } from 'lucide-react';

const PRESETS = [
  { key: '1w', label: '1 week', days: 7 },
  { key: '2w', label: '2 weeks', days: 14 },
  { key: '1m', label: 'Last month', days: 30 },
  { key: 'custom', label: 'Custom', days: null },
];

const fmt = (mins) => {
  if (mins === null || mins === undefined) return '--';
  const m = Math.round(mins);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

const iso = (d) => d.toISOString().split('T')[0];

export default function PerformancePage() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const isAdmin = getUser()?.role === 'admin';

  const [preset, setPreset] = useState('1w');
  const [customStart, setCustomStart] = useState(iso(new Date(Date.now() - 7 * 864e5)));
  const [customEnd, setCustomEnd] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) { navigate('/team'); return; }
    load();
    // eslint-disable-next-line
  }, [preset, customStart, customEnd]);

  const load = async () => {
    setLoading(true);
    try {
      let start, end;
      if (preset === 'custom') {
        start = customStart; end = customEnd;
      } else {
        const days = PRESETS.find((p) => p.key === preset).days;
        end = iso(new Date());
        start = iso(new Date(Date.now() - days * 864e5));
      }
      const res = await getStaffPerformance(staffId, start, end);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const trendDown = data?.trend_pct !== null && data?.trend_pct < 0;
  const trendUp = data?.trend_pct !== null && data?.trend_pct > 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white">
        <div className="max-w-4xl mx-auto px-4 pt-6 pb-8">
          <button onClick={() => navigate('/team')} className="flex items-center gap-1.5 text-white/90 hover:text-white text-sm font-medium mb-4" data-testid="performance-back">
            <ArrowLeft size={18} /> Back to Team
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <BarChart3 size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold leading-tight" data-testid="performance-staff-name">{data?.staff?.name || 'Performance'}</h1>
              <p className="text-white/85 text-sm">Cleaning performance</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Period selector */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="performance-period">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-4 h-10 rounded-xl text-sm font-semibold border-2 transition-colors ${
                preset === p.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 mb-5 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">From</label>
              <input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} className="input-3d h-11 px-3" data-testid="custom-start" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">To</label>
              <input type="date" value={customEnd} min={customStart} max={iso(new Date())} onChange={(e) => setCustomEnd(e.target.value)} className="input-3d h-11 px-3" data-testid="custom-end" />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-slate-400" data-testid="performance-loading">Loading...</div>
        ) : !data || !data.enough_data ? (
          <div className="bg-white rounded-2xl p-10 shadow-sm border border-slate-100 text-center" data-testid="performance-empty">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <BarChart3 size={30} className="text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">Not enough data yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              We need at least 5 completed cleanings with a recorded time in this period to show reliable averages.
              {data ? ` (${data.completed_with_actual} so far)` : ''}
            </p>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="stat-avg">
                <div className="flex items-center gap-2 text-slate-400 mb-2"><Clock size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Avg cleaning time</span></div>
                <div className="text-3xl font-extrabold text-slate-900">{fmt(data.avg_minutes)}</div>
                {data.trend_pct !== null && (
                  <div className={`inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-xs font-bold ${
                    trendDown ? 'bg-emerald-50 text-emerald-700' : trendUp ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'
                  }`} data-testid="stat-trend">
                    {trendDown ? <TrendingDown size={13} /> : trendUp ? <TrendingUp size={13} /> : <Minus size={13} />}
                    {Math.abs(data.trend_pct)}% vs previous
                  </div>
                )}
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="stat-total">
                <div className="flex items-center gap-2 text-slate-400 mb-2"><CheckCircle2 size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Cleanings done</span></div>
                <div className="text-3xl font-extrabold text-slate-900">{data.total_cleanings}</div>
                <p className="text-xs text-slate-400 mt-2">{data.completed_with_actual} with recorded time</p>
              </motion.div>
            </div>

            {/* Optimization tips */}
            {data.tips && data.tips.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-4" data-testid="performance-tips">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb size={18} className="text-amber-500" />
                  <h3 className="text-base font-bold text-amber-800">Optimization tips</h3>
                </div>
                <ul className="space-y-2.5">
                  {data.tips.map((t, i) => (
                    <li key={i} className="flex gap-2 text-sm text-amber-900" data-testid={`tip-${i}`}>
                      <span className="text-amber-400 font-bold flex-shrink-0">•</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Per-property breakdown */}
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">By property</h3>
            <div className="space-y-2.5" data-testid="performance-properties">
              {data.properties.map((p) => {
                const over = p.delta_minutes !== null && p.delta_minutes > 0;
                const under = p.delta_minutes !== null && p.delta_minutes <= 0;
                return (
                  <div key={p.propertyId} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center justify-between" data-testid={`perf-property-${p.propertyId}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-slate-900 font-semibold"><MapPin size={14} className="text-slate-400 flex-shrink-0" /><span className="truncate">{p.name}</span></div>
                      <p className="text-xs text-slate-400 mt-0.5">{p.count} cleaning{p.count !== 1 ? 's' : ''}{p.target_minutes ? ` · target ${fmt(p.target_minutes)}` : ' · no target set'}</p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <div className="text-lg font-extrabold text-slate-900">{fmt(p.avg_minutes)}</div>
                      {p.delta_minutes !== null && (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${over ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                          {over ? '+' : ''}{Math.round(p.delta_minutes)}m {over ? 'over' : 'under'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <MobileNav />
    </div>
  );
}
